import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCalendarEvents } from "@/hooks/use-calendar-events";
import {
  CalendarToolbar,
  type CalendarView,
} from "@/components/calendar/CalendarToolbar";
import { NewEventModal } from "@/components/calendar/NewEventModal";
import {
  addDays,
  isoDate,
  isoMonth,
  isoWeek,
  isoYear,
  parseIsoWeek,
  startOfWeek,
} from "@/lib/calendar-utils";

const VIEW: CalendarView = "week";
const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CalendarWeek() {
  const navigate = useNavigate();
  const { week } = useParams<{ week: string }>();
  const [showNew, setShowNew] = useState(false);
  const [seedStart, setSeedStart] = useState<Date | undefined>(undefined);

  const monday = useMemo(() => (week ? parseIsoWeek(week) : startOfWeek(new Date())), [week]);
  const sunday = addDays(monday, 6);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);

  const rangeEnd = addDays(monday, 7);
  const events = useCalendarEvents(monday, rangeEnd);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, ReturnType<typeof useCalendarEvents>["data"] extends infer R | undefined ? Exclude<R, undefined> : []>();
    for (const e of events.data ?? []) {
      const list = m.get(e.date) ?? [];
      list.push(e);
      m.set(e.date, list);
    }
    return m;
  }, [events.data]);

  function go(delta: number) {
    const next = addDays(monday, delta * 7);
    navigate(`/calendar/week/${isoWeek(next)}`);
  }

  const monthLabel = monday.toLocaleString("default", { month: "long", year: "numeric" });
  const title = `${monthLabel} · Week of ${monday.getDate()}`;

  return (
    <article aria-labelledby="calendar-week-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Calendar · Week
      </p>
      <CalendarToolbar
        title={title}
        view={VIEW}
        isoDay={isoDate(monday)}
        isoWeek={isoWeek(monday)}
        isoMonth={isoMonth(monday)}
        isoYear={isoYear(monday)}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onToday={() => navigate(`/calendar/week/${isoWeek(new Date())}`)}
        onNewEvent={() => {
          setSeedStart(undefined);
          setShowNew(true);
        }}
      />
      <h2 id="calendar-week-heading" style={{ position: "absolute", left: -9999 }}>
        Calendar week
      </h2>

      <div
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-2)",
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
        }}
      >
        {days.map((d, i) => {
          const iso = isoDate(d);
          const isToday = iso === isoDate(new Date());
          const evs = (eventsByDay.get(iso) ?? []) as ReturnType<typeof useCalendarEvents>["data"] extends infer R | undefined ? Exclude<R, undefined> : [];
          return (
            <div
              key={iso}
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: 200,
                padding: 6,
                background: "var(--bg-3)",
                borderRadius: "var(--radius-chip)",
                border: isToday ? "1px solid var(--accent)" : "1px solid transparent",
              }}
            >
              <header
                onDoubleClick={() => {
                  setSeedStart(d);
                  setShowNew(true);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSeedStart(d);
                  setShowNew(true);
                }}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 6,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", textTransform: "uppercase" }}>
                  {DOW_LABELS[i]}
                </span>
                <Link
                  to={`/calendar/day/${iso}`}
                  className="mono"
                  style={{
                    color: isToday ? "var(--accent)" : "var(--fg)",
                    fontSize: "var(--type-body-s)",
                    textDecoration: "none",
                  }}
                >
                  {d.getDate()}
                </Link>
              </header>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 3 }}>
                {evs.slice(0, 6).map((e) => (
                  <li
                    key={e.id}
                    style={{
                      fontSize: "var(--type-micro)",
                      color: kindColor(e.kind),
                      padding: "2px 4px",
                      borderRadius: 3,
                      background: kindBg(e.kind),
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={e.title}
                  >
                    {e.ts && (
                      <span className="mono" style={{ color: "var(--fg-3)", marginRight: 4 }}>
                        {new Date(e.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                    {e.title}
                  </li>
                ))}
                {evs.length > 6 && (
                  <li style={{ fontSize: "var(--type-micro)", color: "var(--fg-3)" }}>
                    +{evs.length - 6} more
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>

      <p
        style={{
          marginTop: "var(--space-3)",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.04em",
        }}
      >
        Shortcuts: ←/→ prev/next week · T today · D/M/Y switch view · N new event ·
        Double-click a day to schedule.
      </p>

      <NewEventModal
        open={showNew}
        defaultStart={seedStart}
        onClose={() => setShowNew(false)}
      />
    </article>
  );
  // sunday param kept available for future range labels
  void sunday;
}

function kindColor(kind: string): string {
  switch (kind) {
    case "user_event":
      return "var(--accent)";
    case "external":
      return "var(--fg)";
    case "session":
      return "var(--macro-fat)";
    case "meal":
      return "var(--amber)";
    case "project_deadline":
      return "var(--brick)";
    default:
      return "var(--fg-2)";
  }
}

function kindBg(kind: string): string {
  switch (kind) {
    case "user_event":
      return "var(--accent-wash)";
    case "external":
      return "var(--chip)";
    case "session":
      return "color-mix(in oklab, var(--macro-fat) 12%, transparent)";
    case "meal":
      return "color-mix(in oklab, var(--amber) 12%, transparent)";
    case "project_deadline":
      return "color-mix(in oklab, var(--brick) 14%, transparent)";
    default:
      return "transparent";
  }
}
